import {
  AfterViewInit,
  Component,
  Inject,
  Input,
  NgZone,
  PLATFORM_ID,
} from '@angular/core';
import { CredentialResponse, accounts } from 'google-one-tap';
import { CtxForm } from '../../_forms/models/form.model';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ScriptService } from '../../_services/script.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ThemeManagerService } from '../../_services/theme-manager.service';
import { Router } from '@angular/router';
import { AuthnService } from '../../_services/authn.service';

import { projectEnv } from 'utils';

declare const google: any;

@Component({
  selector: 'ctx-google-sso',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule],
  templateUrl: './google-sso.component.html',
})
export class GoogleSsoComponent implements AfterViewInit {
  @Input() cardContent: HTMLElement;
  @Input() parentForm: CtxForm;

  /**
   * Shows the Google SSO button programatically instead of using Google's default window.onload hook.
   */
  constructor(
    private scriptService: ScriptService,
    private themeService: ThemeManagerService,
    private ngZone: NgZone,
    private jwtHelper: JwtHelperService,
    private router: Router,
    private authnService: AuthnService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {}

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId) && this.enableGoogleSSO) {
      this.parentForm.setState('gsi', true);
      this.scriptService.loadGsi().then((loaded) => {
        if (loaded) {
          this.showGoogleButton();
          this.googleLoad = true;
        }
        this.parentForm.setState('gsi', false);
      });
    }
  }

  get enableGoogleSSO() {
    return projectEnv.get('googleClientId');
  }
  googleLoad = false;

  showGoogleButton() {
    if (google && google.accounts && this.enableGoogleSSO) {
      const accounts = google.accounts as accounts;
      const width = this.cardContent?.offsetWidth
        ? this.cardContent?.offsetWidth - 48
        : 334;

      accounts.id.initialize({
        client_id: projectEnv.get('googleClientId'),
        callback: this.loginWithGoogleToken.bind(this),
      });
      const theme = this.themeService.isDarkMode ? 'filled_black' : 'outline';
      accounts.id.renderButton(
        document.getElementById('googleButtonDiv') as HTMLElement,
        {
          theme,
          size: 'medium',
          type: 'standard',
          shape: 'rectangular',
          text: 'continue_with',
          width,
        }
      );
    }
  }

  /**
   * Callback for GSI login. Since the GSI library, the caller of this callback, executes this function
   * synchronously, we must set it up in a synchronous manner.
   *
   * Since this callback is called outside Angular by the GSI library, we must run it using ngZone to
   * ensure change detection works correctly.
   */
  loginWithGoogleToken(credentialResponse: CredentialResponse): void {
    this.ngZone.run(async () => {
      this.parentForm.setState('disabled', true);
      const email: string = this.jwtHelper.decodeToken(
        credentialResponse.credential
      ).email;
      this.authnService
        .loginWithGoogleToken(
          await this.authnService.getAccountAlias(email),
          credentialResponse
        )
        .then(() => {
          this.parentForm.setCustomApiMessage({
            alertLevel: 'success',
            text: 'Login with Google successful. Logging you in.',
          });
          this.router.navigate(['/']);
        })
        .catch((errorResponse) => {
          this.parentForm.setApiMessage(errorResponse);
          this.parentForm.setState('disabled', false);
        });
    });
  }
}
